import React from "react";
import { motion } from "framer-motion";
import { Target } from "lucide-react";

const problems = [
  "Confusion between course, country, and career outcomes",
  "Weak positioning in SOP and profile",
  "Missed deadlines and wrong shortlists",
  "Test planning done too late",
  "Visa stress and documentation gaps",
];

const promise = [
  "Counselling-first. Clarity before decisions.",
  "ROI and scholarship focus — outcomes over hype.",
  "A partner to your family's decisions. No conflicts. No hidden incentives.",
  "Strategy plus execution. Flawless. On time.",
  "Honest guidance, including a clear no when the fit is wrong.",
];

const fadeUp = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } };

export default function ProblemPromise() {
  return (
    <section
      id="problem"
      data-testid="problem-promise-section"
      style={{ padding: "100px 0", backgroundColor: "#fff" }}
    >
      <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "0 24px" }}>
        <div style={{ gap: "80px" }} className="mw-grid-2">

          {/* Left: Problem */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={{ show: { transition: { staggerChildren: 0.1 } } }}
          >
            <motion.div variants={fadeUp} style={{ marginBottom: "14px" }}>
              <span style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.12em", textTransform: "uppercase", color: "#F5A000", fontFamily: "'DM Sans', sans-serif" }}>
                The Planning Gap
              </span>
            </motion.div>
            <motion.h2
              variants={fadeUp}
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(26px, 2.5vw, 36px)",
                fontWeight: "700",
                color: "#0F172A",
                lineHeight: "1.25",
                margin: "0 0 12px",
              }}
            >
              Most students do not struggle because of capability.
            </motion.h2>
            <motion.p
              variants={fadeUp}
              style={{ fontSize: "17px", color: "#64748B", fontFamily: "'DM Sans', sans-serif", margin: "0 0 32px", lineHeight: "1.65" }}
            >
              They struggle because they <strong style={{ color: "#0F172A" }}>start too late and plan too loosely.</strong>
            </motion.p>

            <motion.div variants={fadeUp} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              {problems.map((p, i) => (
                <div key={i} data-testid={`problem-item-${i}`} style={{ display: "flex", gap: "14px", alignItems: "flex-start" }}>
                  <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#F5A000", marginTop: "8px", flexShrink: 0 }} />
                  <p style={{ fontSize: "16px", color: "#475569", fontFamily: "'DM Sans', sans-serif", margin: 0, lineHeight: "1.65" }}>{p}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right: Promise */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div style={{
              backgroundColor: "#2B2F9E",
              borderRadius: "16px",
              padding: "48px 40px",
              color: "#fff",
              position: "relative",
              overflow: "hidden",
            }}>
              <div style={{ position: "absolute", top: "-60px", right: "-60px", width: "200px", height: "200px", borderRadius: "50%", backgroundColor: "rgba(245,160,0,0.12)" }} />
              <Target size={40} style={{ color: "#F5A000", marginBottom: "20px" }} />
              <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "26px", fontWeight: "700", margin: "0 0 8px", lineHeight: "1.25" }}>
                The Mindways Promise
              </h3>
              <p style={{ fontSize: "13px", fontWeight: "600", color: "#F5A000", fontFamily: "'DM Sans', sans-serif", textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 24px" }}>
                A partner to your family's decisions.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                {promise.map((point) => (
                  <div key={point} style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                    <div style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "#F5A000", marginTop: "8px", flexShrink: 0 }} />
                    <span style={{ fontSize: "15px", fontFamily: "'DM Sans', sans-serif", color: "rgba(255,255,255,0.92)", lineHeight: "1.55" }}>{point}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
